import {
  clamp,
  type QueriesObject,
  type QueryResults,
  type System,
} from '../../odie';
import { BlockComponent } from '../components/BlockComponent';
import { SodaBlockComponent } from '../components/SodaBlockComponent';
import { PhaseComponent } from '../components/PhaseComponent';
import { BasicBlockProps } from '../defs/block';
import {
  BlockEntity,
  setBlockEntityViewLayer,
  SodaBlockEntity,
} from '../entities/BlockEntity';
import type { GameScene } from '../GameScene';
import { SodaColor } from '../views/blocks/components/SodaBottle';
import { DespawnSource } from './DespawnSystem';
import { TravelHitEffect } from '../actions/effects/TravelHitEffect';
import gsap from 'gsap';
import { config } from '../defs/config';

/*
  Handles soda block specifics.
*/
export class SodaBlockSystem implements System {
  public static readonly NAME = 'blockSoda';
  public static Queries: QueriesObject = {
    block: {
      components: [BlockComponent, SodaBlockComponent],
    },
    phase: {
      components: [PhaseComponent],
      modified: true,
    },
  };
  public scene!: GameScene;
  public queries!: QueryResults;
  public animationCount = 0;

  public start() {
    // Sodas are hit by basic blocks, so they need to animate these matched basic blocks before despawning them
    this.scene.despawnSystem.addDespawnInterceptor(this);
  }

  public async interceptDespawn(entity: BlockEntity, source: DespawnSource) {
    const isBasiBlock = entity.c.block.props.type === 'basic';
    // Skip if not a basic block
    if (!isBasiBlock) return false;
    // Find a suitable target soda for the block
    const targets = this.getTargetsFromBlock(entity);
    // Skip if there is no valid target
    if (!targets.length) return false;
    // Animate the block hitting the target soda
    await this.hitTarget(
      entity,
      targets[0],
      source === 'mergeEffect' ? 0.1 : 0,
    );
    // Return true to tell that the despawn has been intercepted
    return true;
  }

  private async hitTarget(
    block: BlockEntity,
    target: SodaBlockEntity,
    delayBetweenBlocks: number,
  ) {
    if (!target) return;
    // Decrement the soda hit points, for target selecting purposes. The actual hit will happen after the animation
    target.c.blockSoda.hitPoints -= 1;
    // Calculate delay based on animation count
    const delay = clamp(delayBetweenBlocks * this.animationCount, 0, 0.5);
    // Increment animation count for calculating the delay
    this.animationCount += 1;
    // Tween params
    const dx = target.x - block.x;
    const dy = target.y - block.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const durationPerCell = 0.1;
    const duration = (dist / config.tile.size) * durationPerCell;
    const targetScale = 0.6;
    const enlargeScale = 1.25;
    const targetPos = {
      x: target.x + target.c.blockSoda.getBottomOffsetX(targetScale),
      y: target.y + target.c.blockSoda.getBottomOffsetY(targetScale),
    };

    await new TravelHitEffect(this.scene, {
      block,
      target: targetPos,
      duration,
      delay,
      customTween: () => {
        const { x, y } = targetPos;
        const direction = target.c.blockSoda.direction;
        const primaryAxis =
          direction === 'up' || direction === 'down' ? { x } : { y };
        const secondaryAxis =
          direction === 'up' || direction === 'down' ? { y } : { x };

        return gsap
          .timeline({
            delay,
            onStart: () => {
              setBlockEntityViewLayer(this.scene, block, 'foreground');
            },
          })
          .to(block, {
            duration,
            ...primaryAxis,
          })
          .to(
            block,
            {
              duration,
              ...secondaryAxis,
              ease: 'quad.in',
            },
            0,
          )
          .to(
            block,
            {
              duration: duration * 0.5,
              scaleX: enlargeScale,
              scaleY: enlargeScale,
            },
            0,
          )
          .to(
            block,
            {
              duration: duration * 0.5,
              scaleX: targetScale,
              scaleY: targetScale,
            },
            duration * 0.5,
          );
      },
    }).execute();
    // Hit the soda, actually decrementing the counter
    target.c.block.collide('goal');
    // Decrement animation count after finish
    this.animationCount -= 1;
  }

  private getTargetsFromBlock(entity: BlockEntity): SodaBlockEntity[] {
    const props = entity.c.block.props as BasicBlockProps;
    const color = props.color as SodaColor;
    // Sort available sodas by progress and distance
    return this.getTargetsFromColor(color).sort((a, b) => {
      const dax = a.x - entity.x;
      const day = a.y - entity.y;
      const dbx = b.x - entity.x;
      const dby = b.y - entity.y;
      const distA = Math.sqrt(dax * dax + day * day);
      const distB = Math.sqrt(dbx * dbx + dby * dby);
      const dc = a.c.blockSoda.hitPoints - b.c.blockSoda.hitPoints;
      const dd = distA - distB;

      // Sort by existing progress first, then by distance.
      return dd + dc * 1000;
    });
  }

  private getTargetsFromColor(color: SodaColor): SodaBlockEntity[] {
    const entities = this.queries.block.entities as SodaBlockEntity[];
    // Return all sodas with available hit points
    return entities.filter(
      (e) => e.c.blockSoda.color === color && e.c.blockSoda.hitPoints > 0,
    );
  }
}
